<template>
  <div
    class="lg:ml-56 lg:mr-44 min-h-screen bg-bluegray-100 w-full p-8 lg:p-16"
  >
    <div class="">
      <h2 class="cw-text font-bold mb-0 relative top-2">CareerWise</h2>
      <h1 class="mb-8">Statistics Dashboard</h1>
    </div>

    <div class="relative mb-16">
      <div class="relative text-truegray-500 flex flex-col text-sm">
        <span class="mb-4">
          As part of the
          <a
            class="text-cw-700 text-sm font-secondary no-underline font-bold"
            target="blank"
            href="https://careerwise.school"
            >CareerWise</a
          >
          integration with NZ Careers Expo, the CareerWise Statistics Dashboard
          is your insight into the performance your posts have had in the
          CareerWise Cloud.
          <span
            class="text-xs text-cw-alt relative mb-2 cursor-pointer"
            @click="cloudInfoToggle = !cloudInfoToggle"
            >what's this?</span
          >
        </span>
        <div
          v-show="cloudInfoToggle"
          class="absolute left-0 top-16 rounded-xl border border-truegray-300 p-6 w-8/12 bg-white"
        >
          <p class="text-xs text-truegray-700 mb-4">
            The CareerWise Cloud is the engine behind CareerWise, where Careers
            Advisors around the country go to view the latest news, events, and
            jobs happening in the careers space to share with their students.
          </p>
          <p class="text-xs text-truegray-700">
            It consists of posts shared by schools, businesses, education
            providers and everything inbetween to create a collaborative space
            with all the latest careers information ready to be sent to their
            students and parents in one click.
          </p>
        </div>
        <span>
          Your posts will be shown on your profile page, the Online Expo, and
          also in the CareerWise Cloud. However, the statistics shown below only
          pertain to the CareerWise Cloud.
        </span>
      </div>
    </div>

    <!-- Totals -->
    <h3 class="mb-4">Totals</h3>
    <div v-if="totalsLoading" class="h-32 w-32">
      <lottie :options="loadingAnimationOptions" />
    </div>
    <div v-else class="flex space-x-10">
      <div
        class="rounded-xl bg-white text-center flex flex-col items-center justify-center w-48 p-3 shadow"
      >
        <div class="flex items-center text-black">
          <h5>Posts</h5>
        </div>
        <h2 class="font-semibold m-0">{{ totals.posts }}</h2>
      </div>
      <div
        class="rounded-xl bg-white text-center flex flex-col items-center justify-center w-48 p-3 shadow"
      >
        <div class="flex items-center text-black">
          <h5>Shares</h5>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"
            />
          </svg>
        </div>
        <h2 class="font-semibold m-0 text-cw-alt">{{ totals.shares }}</h2>
      </div>
      <div
        class="rounded-xl bg-white text-center flex flex-col items-center justify-center w-48 p-3 shadow"
      >
        <div class="flex items-center text-black">
          <h5>Potential Reach</h5>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <h2 class="font-semibold m-0 text-cw-alt">{{ totals.audience }}</h2>
      </div>
    </div>

    <!-- Recent Activity -->
    <h3 class="mb-4 mt-12">Recent Activity</h3>
    <div v-if="postsLoading" class="h-32 w-32">
      <lottie :options="loadingAnimationOptions" />
    </div>

    <p v-if="posts.length < 1 && !postsLoading">Nothing to display...</p>

    <div class="bg-white w-full rounded-xl">
      <div
        v-for="(post, i) in posts"
        :key="i"
        :ref="'posts-' + i"
        class="flex text-xs h-12"
      >
        <div class="w-1/2 flex items-center">
          <div
            v-if="current_page > 1"
            class="w-16 justify-center flex items-center"
          >
            #{{ i + 1 + (current_page * per_page - per_page) }}
          </div>
          <div v-else class="w-16 justify-center flex items-center">
            #{{ i + 1 }}
          </div>
          <div class="h-1/2 w-px bg-bluegray-300"></div>
          <p class="mx-4 truncate overflow-ellipsis text-xs lg:text-sm">
            {{ post.title }}
          </p>
        </div>

        <div
          class="w-1/2 flex items-center pr-2 lg:pr-0 font-xs lg:font-medium"
        >
          <div class="flex items-center w-2/4 lg:w-1/3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-3 w-3 lg:h-5 lg:w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"
              />
            </svg>
            <p class="font-semibold text-xs lg:text-sm text-cw-alt">
              {{ post.shares }}
            </p>
          </div>
          <div class="flex items-center w-2/4 lg:w-1/3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-3 w-3 lg:h-5 lg:w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clip-rule="evenodd"
              />
            </svg>
            <p class="font-semibold text-xs lg:text-sm text-cw-alt">
              {{ post.audience }}
            </p>
          </div>
          <div
            class="text-cw-alt cursor-pointer w-1/4 lg:w-1/3 flex justify-center items-center space-x-1"
            @click="goToPost(post.id)"
          >
            <span class="w-full text-right px-4"
              >View <span class="hidden lg:inline">post ></span></span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- PAGINATION -->
    <div
      v-if="pagination.previouspage || pagination.nextpage"
      class="flex justify-between items-center h-10"
    >
      <span
        :disabled="!pagination.previouspage"
        :class="[
          !pagination.previouspage
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
        ]"
        class="text-gray-500"
        @click="getStatistics(pagination.previouspage)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 17l-5-5m0 0l5-5m-5 5h12"
          />
        </svg>
      </span>
      <span class="text-center text-xs"
        >Page {{ pagination.current_page }} of
        {{ pagination.total_pages }}</span
      >
      <span
        :disabled="!pagination.nextpage"
        :class="[
          !pagination.nextpage
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
        ]"
        class="text-gray-500"
        @click="getStatistics(pagination.nextpage)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as loadingAnimation from '../../../shared/components/loading.json'

export default {
  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      cloudInfoToggle: false,

      totalsLoading: true,
      totals: {
        posts: 0,
        shares: 0,
        audience: 0,
      },

      postsLoading: true,
      posts: [],

      current_page: null,
      per_page: 0,

      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },
    }
  },

  computed: {
    ...mapGetters({
      client: 'shared/getClient',
      filterResourceBy: 'shared/getFilterResourceBy',
    }),
  },

  mounted() {
    axios.get('/api/auth/post-statistics-total').then((response) => {
      let result = response.data
      this.$set(this, 'totalsLoading', true)
      if (result.success) {
        this.$set(this, 'totals', result.totals)
        this.$set(this, 'totalsLoading', false)
      }
    })

    this.getStatistics()
  },

  methods: {
    goToPost(id) {
      this.$router.push({ path: `/post/${id}` })
    },

    getStatistics(gotopage = null) {
      let submitData = {}

      submitData.search = this.filterResourceBy
      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      submitData.client = this.client

      this.postsLoading = true

      // Get this post...
      axios
        .post('/api/auth/post-statistics-list', submitData)
        .then((response) => {
          let result = response.data
          if (result.success) {
            this.$set(this, 'posts', result.posts)

            this.$set(this, 'postsLoading', false)

            // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
            let total_pages =
              Math.floor(
                response.data.pagination.total /
                  response.data.pagination.per_page,
              ) + 1
            this.$set(
              this.pagination,
              'current_page',
              response.data.pagination.current_page,
            )
            this.$set(this.pagination, 'total_pages', total_pages)
            this.$set(
              this.pagination,
              'per_page',
              response.data.pagination.per_page,
            )
            this.$set(
              this.pagination,
              'last_page_url',
              response.data.pagination.last_page_url,
            )
            this.$set(
              this.pagination,
              'next_page_url',
              response.data.pagination.next_page_url,
            )
            this.$set(
              this.pagination,
              'prev_page_url',
              response.data.pagination.prev_page_url,
            )
            this.$set(
              this.pagination,
              'path',
              response.data.pagination.next_page_path,
            )

            this.$set(
              this,
              'current_page',
              response.data.pagination.current_page,
            )
            this.$set(this, 'per_page', response.data.pagination.per_page)

            let prev = null
            if (this.pagination.prev_page_url) {
              prev = this.pagination.prev_page_url.split('=')
              prev = prev[1]
            }

            let next = null
            if (this.pagination.next_page_url) {
              next = this.pagination.next_page_url.split('=')
              next = next[1]
            }

            this.$set(this.pagination, 'previouspage', prev)
            this.$set(this.pagination, 'nextpage', next)
          }
        })
    },
  },
}
</script>

<style scoped>
.cw-text {
  background: -webkit-linear-gradient(
    45deg,
    var(--cw-700),
    var(--cw-alt),
    var(--cw-alt)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 60px;
}
</style>
